import React, { useState, useEffect } from 'react';
import './Settings.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';

function TeamManagement({ onBack }) {
    const [teams, setTeams] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [editingTeam, setEditingTeam] = useState(null);
    const [formData, setFormData] = useState({
        team_code: '',
        team_name: '',
        description: '',
        is_active: true,
        display_order: 0
    });

    useEffect(() => {
        fetchTeams();
    }, []);

    const fetchTeams = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('authToken');
            const response = await fetch(`${API_BASE_URL}/api/crm/teams`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) {
                throw new Error('Failed to fetch teams');
            }

            const data = await response.json();
            setTeams(data.data || []);
            setError(null);
        } catch (err) {
            setError(err.message);
            console.error('Error fetching teams:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleOpenModal = (team = null) => {
        if (team) {
            setEditingTeam(team);
            setFormData({
                team_code: team.team_code,
                team_name: team.team_name,
                description: team.description || '',
                is_active: team.is_active,
                display_order: team.display_order || 0
            });
        } else {
            setEditingTeam(null);
            setFormData({
                team_code: '',
                team_name: '',
                description: '',
                is_active: true,
                display_order: 0
            });
        }
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setEditingTeam(null);
        setFormData({
            team_code: '',
            team_name: '',
            description: '',
            is_active: true,
            display_order: 0
        });
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        try {
            const token = localStorage.getItem('authToken');
            const url = editingTeam
                ? `${API_BASE_URL}/api/crm/teams/${editingTeam.id}`
                : `${API_BASE_URL}/api/crm/teams`;

            const method = editingTeam ? 'PUT' : 'POST';

            const response = await fetch(url, {
                method,
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(formData)
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to save team');
            }

            await fetchTeams();
            handleCloseModal();
        } catch (err) {
            alert(err.message);
            console.error('Error saving team:', err);
        }
    };

    const handleDelete = async (teamId) => {
        if (!window.confirm('Are you sure you want to delete this team?')) {
            return;
        }

        try {
            const token = localStorage.getItem('authToken');
            const response = await fetch(`${API_BASE_URL}/api/crm/teams/${teamId}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to delete team');
            }

            await fetchTeams();
        } catch (err) {
            alert(err.message);
            console.error('Error deleting team:', err);
        }
    };

    return (
        <div className="settings-component-wrapper">
            <button className="back-button" onClick={onBack}>
                <span className="back-icon">←</span>
                <span>Back to HR Settings</span>
            </button>

            <div className="settings-component-content">
                <div className="settings-component-header">
                    <h1>👥 Team Management</h1>
                    <p>Configure HR teams and member assignments</p>
                </div>

                <div className="settings-component-body">
                    <div className="settings-action-bar">
                        <button
                            className="settings-primary-button"
                            onClick={() => handleOpenModal()}
                        >
                            + Add New Team
                        </button>
                    </div>

                    {loading && (
                        <div className="settings-loading-state">
                            <span>Loading teams...</span>
                        </div>
                    )}

                    {error && (
                        <div className="settings-error-state">
                            <span>Error: {error}</span>
                        </div>
                    )}

                    {!loading && !error && teams.length === 0 && (
                        <div className="settings-empty-state">
                            <span className="empty-icon">📋</span>
                            <p>No teams found. Create your first team to get started.</p>
                        </div>
                    )}

                    {!loading && !error && teams.length > 0 && (
                        <div className="settings-table-container">
                            <table className="settings-table">
                                <thead>
                                    <tr>
                                        <th>Team Code</th>
                                        <th>Team Name</th>
                                        <th>Description</th>
                                        <th>Display Order</th>
                                        <th>Status</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {teams.map(team => (
                                        <tr key={team.id}>
                                            <td>{team.team_code}</td>
                                            <td>{team.team_name}</td>
                                            <td>{team.description || '-'}</td>
                                            <td>{team.display_order}</td>
                                            <td>
                                                <span className={`settings-status-badge ${team.is_active ? 'active' : 'inactive'}`}>
                                                    {team.is_active ? 'Active' : 'Inactive'}
                                                </span>
                                            </td>
                                            <td>
                                                <div className="settings-action-buttons">
                                                    <button
                                                        className="settings-edit-button"
                                                        onClick={() => handleOpenModal(team)}
                                                        title="Edit team"
                                                    >
                                                        ✏️
                                                    </button>
                                                    <button
                                                        className="settings-delete-button"
                                                        onClick={() => handleDelete(team.id)}
                                                        title="Delete team"
                                                    >
                                                        🗑️
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            {showModal && (
                <div className="settings-modal-overlay" onClick={handleCloseModal}>
                    <div className="settings-modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="settings-modal-header">
                            <h2>{editingTeam ? 'Edit Team' : 'Add New Team'}</h2>
                            <button className="settings-close-button" onClick={handleCloseModal}>×</button>
                        </div>
                        <form onSubmit={handleSubmit}>
                            <div className="settings-form-group">
                                <label htmlFor="team_code">Team Code *</label>
                                <input
                                    type="text"
                                    id="team_code"
                                    name="team_code"
                                    value={formData.team_code}
                                    onChange={handleInputChange}
                                    required
                                    placeholder="e.g., HR, IT, FIN"
                                />
                            </div>

                            <div className="settings-form-group">
                                <label htmlFor="team_name">Team Name *</label>
                                <input
                                    type="text"
                                    id="team_name"
                                    name="team_name"
                                    value={formData.team_name}
                                    onChange={handleInputChange}
                                    required
                                    placeholder="e.g., Human Resources"
                                />
                            </div>

                            <div className="settings-form-group">
                                <label htmlFor="description">Description</label>
                                <textarea
                                    id="description"
                                    name="description"
                                    value={formData.description}
                                    onChange={handleInputChange}
                                    rows="3"
                                    placeholder="Brief description of the team"
                                />
                            </div>

                            <div className="settings-form-group">
                                <label htmlFor="display_order">Display Order</label>
                                <input
                                    type="number"
                                    id="display_order"
                                    name="display_order"
                                    value={formData.display_order}
                                    onChange={handleInputChange}
                                    min="0"
                                />
                            </div>

                            <div className="settings-form-group settings-checkbox-group">
                                <label>
                                    <input
                                        type="checkbox"
                                        name="is_active"
                                        checked={formData.is_active}
                                        onChange={handleInputChange}
                                    />
                                    <span>Active</span>
                                </label>
                            </div>

                            <div className="settings-modal-actions">
                                <button type="button" className="settings-cancel-button" onClick={handleCloseModal}>
                                    Cancel
                                </button>
                                <button type="submit" className="settings-submit-button">
                                    {editingTeam ? 'Update Team' : 'Create Team'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}

export default TeamManagement;
